import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "prompt-panel" }
const _hoisted_2 = { class: "search-section" }
const _hoisted_3 = { class: "input-group" }
const _hoisted_4 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_5 = {
  key: 1,
  class: "error-state"
}
const _hoisted_6 = { class: "text-danger" }
const _hoisted_7 = {
  key: 2,
  class: "categories-list"
}
const _hoisted_8 = {
  key: 0,
  class: "no-results"
}
const _hoisted_9 = ["onDragstart", "onClick"]
const _hoisted_10 = { class: "category-title" }
const _hoisted_11 = {
  key: 0,
  class: "badge bg-primary"
}
const _hoisted_12 = {
  key: 0,
  class: "variables-container"
}
const _hoisted_13 = {
  key: 0,
  class: "variables-loading"
}
const _hoisted_14 = {
  key: 1,
  class: "variables-error"
}
const _hoisted_15 = { class: "text-danger" }
const _hoisted_16 = {
  key: 2,
  class: "variables-list"
}
const _hoisted_17 = ["onDragstart"]
const _hoisted_18 = { class: "variable-header" }
const _hoisted_19 = { class: "variable-content" }
const _hoisted_20 = { class: "variable-title" }
const _hoisted_21 = {
  key: 0,
  class: "variable-description"
}
const _hoisted_22 = { class: "variable-meta" }
const _hoisted_23 = {
  key: 0,
  class: "badge bg-secondary text-xs"
}
const _hoisted_24 = {
  key: 3,
  class: "no-variables"
}

import { ref, computed, onMounted, watch } from "vue";
import apiService from "@/services/apiService";

// Props
interface Category {
  _id: string;
  title: string;
  variableCount?: number;
}

interface Variable {
  _id: string;
  prompt_category_id: string;
  title: string;
  description: string;
  type_id: string;
  icon: string | null;
  slug: string;
  filterable: boolean;
  visible_on_card: boolean;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  updated_by: string | null;
  deleted_by: string | null;
  is_trash: boolean;
  __v: number;
}

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'PromptPanel',
  props: {
    isActive: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

// Types
const categories = ref<Category[]>([]);
const categoryVariables = ref<Map<string, Variable[]>>(new Map());
const expandedCategories = ref<Set<string>>(new Set());
const loadingVariables = ref<Set<string>>(new Set());
const variableErrors = ref<Map<string, string>>(new Map());
const isLoading = ref(false);
const error = ref<string | null>(null);
const searchQuery = ref("");

// Computed
const filteredCategories = computed(() => {
  if (!searchQuery.value.trim()) {
    return categories.value;
  }

  const query = searchQuery.value.toLowerCase();
  return categories.value.filter((category) => {
    // Search in category title
    if (category.title.toLowerCase().includes(query)) {
      return true;
    }

    // Search in variables if loaded
    const variables = categoryVariables.value.get(category._id);
    if (variables) {
      return variables.some(
        (variable) =>
          variable.title.toLowerCase().includes(query) ||
          variable.description?.toLowerCase().includes(query) ||
          variable.slug?.toLowerCase().includes(query)
      );
    }

    return false;
  });
});

// Methods
const loadCategories = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await apiService.get("/common/prompt-categories");

    // Handle new response structure with data property
    if (response && response.data && Array.isArray(response.data)) {
      categories.value = response.data;
    } else if (Array.isArray(response)) {
      // Fallback for old response structure
      categories.value = response;
    } else {
      categories.value = [];
    }
  } catch (err: any) {
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load categories";
  } finally {
    isLoading.value = false;
  }
};

const loadVariables = async (categoryId: string) => {
  loadingVariables.value.add(categoryId);
  variableErrors.value.delete(categoryId);

  try {
    const response = await apiService.get(
      `/common/prompt-variables-filter?prompt_category_id=${categoryId}`
    );

    // Handle new response structure with data property
    let variables: Variable[] = [];
    if (response && response.data && Array.isArray(response.data)) {
      variables = response.data;
    } else if (Array.isArray(response)) {
      // Fallback for old response structure
      variables = response;
    }

    categoryVariables.value.set(categoryId, variables);

    // Update category variable count
    const category = categories.value.find((c) => c._id === categoryId);
    if (category) {
      category.variableCount = variables.length;
    }
  } catch (err: any) {
    const errorMsg =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load variables";
    variableErrors.value.set(categoryId, errorMsg);
  } finally {
    loadingVariables.value.delete(categoryId);
  }
};

const toggleCategory = async (categoryId: string) => {
  if (expandedCategories.value.has(categoryId)) {
    expandedCategories.value.delete(categoryId);
  } else {
    expandedCategories.value.add(categoryId);

    // Load variables if not already loaded
    if (
      !categoryVariables.value.has(categoryId) &&
      !loadingVariables.value.has(categoryId)
    ) {
      await loadVariables(categoryId);
    }
  }
};

const handleSearch = () => {
  // Search is handled by computed property
};

const clearSearch = () => {
  searchQuery.value = "";
};

const handleDragStart = (variable: Variable, event: DragEvent) => {
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/json", JSON.stringify(variable));
    event.dataTransfer.setData("text/plain", variable.title);
    event.dataTransfer.setData("item-type", "variable");
  }
};

const handleCategoryDragStart = async (
  category: Category,
  event: DragEvent
) => {
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "copy";

    // Load variables if not already loaded
    if (!categoryVariables.value.has(category._id)) {
      await loadVariables(category._id);
    }

    const variables = categoryVariables.value.get(category._id) || [];

    const categoryData = {
      category: category,
      variables: variables,
    };

    event.dataTransfer.setData(
      "application/json",
      JSON.stringify(categoryData)
    );
    event.dataTransfer.setData("text/plain", category.title);
    event.dataTransfer.setData("item-type", "category");
  }
};

// Lifecycle
onMounted(() => {
  loadCategories();
});

// Watch isActive prop to reload categories when tab becomes visible
watch(
  () => props.isActive,
  (newVal, oldVal) => {
    if (newVal && !oldVal && categories.value.length === 0) {
      loadCategories();
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "panel-header" }, [
      _createElementVNode("h5", { class: "mb-0" }, [
        _createElementVNode("i", { class: "fa fa-lightbulb me-2" }),
        _createTextVNode(" Prompt Variables ")
      ])
    ], -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "input-group-text" }, [
          _createElementVNode("i", { class: "fa fa-search" })
        ], -1)),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
          type: "text",
          class: "form-control",
          placeholder: "Search categories or variables...",
          onInput: handleSearch
        }, null, 544), [
          [_vModelText, searchQuery.value]
        ]),
        (searchQuery.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-outline-secondary",
              type: "button",
              onClick: clearSearch
            }, [...(_cache[1] || (_cache[1] = [
              _createElementVNode("i", { class: "fa fa-times" }, null, -1)
            ]))]))
          : _createCommentVNode("", true)
      ])
    ]),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[3] || (_cache[3] = [
          _createElementVNode("div", {
            class: "spinner-border text-primary",
            role: "status"
          }, [
            _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
          ], -1),
          _createElementVNode("p", { class: "mt-2 text-muted" }, "Loading categories...", -1)
        ]))]))
      : _createCommentVNode("", true),
    (!isLoading.value && error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _cache[5] || (_cache[5] = _createElementVNode("i", { class: "fa fa-exclamation-triangle text-danger fa-2x mb-2" }, null, -1)),
          _createElementVNode("p", _hoisted_6, _toDisplayString(error.value), 1),
          _createElementVNode("button", {
            class: "btn btn-sm btn-primary",
            onClick: loadCategories
          }, [...(_cache[4] || (_cache[4] = [
            _createElementVNode("i", { class: "fa fa-refresh me-1" }, null, -1),
            _createTextVNode(" Retry ", -1)
          ]))])
        ]))
      : _createCommentVNode("", true),
    (!isLoading.value && !error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          (filteredCategories.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[6] || (_cache[6] = [
                _createElementVNode("i", { class: "fa fa-search fa-2x text-muted mb-2" }, null, -1),
                _createElementVNode("p", { class: "text-muted" }, "No categories found", -1)
              ]))]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredCategories.value, (category) => {
            return (_openBlock(), _createElementBlock("div", {
              key: category._id,
              class: "category-item"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["category-header", { active: expandedCategories.value.has(category._id) }]),
                draggable: "true",
                onDragstart: ($event: any) => (handleCategoryDragStart(category, $event)),
                onClick: ($event: any) => (toggleCategory(category._id))
              }, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("i", {
                    class: _normalizeClass(["fa me-2", 
                expandedCategories.value.has(category._id)
                  ? 'fa-chevron-down'
                  : 'fa-chevron-right'
              ])
                  }, null, 2),
                  _createElementVNode("span", null, _toDisplayString(category.title), 1)
                ]),
                (category.variableCount !== undefined)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(category.variableCount), 1))
                  : _createCommentVNode("", true)
              ], 42, _hoisted_9),
              (expandedCategories.value.has(category._id))
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    (loadingVariables.value.has(category._id))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [...(_cache[7] || (_cache[7] = [
                          _createElementVNode("div", {
                            class: "spinner-border spinner-border-sm text-primary",
                            role: "status"
                          }, [
                            _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
                          ], -1),
                          _createElementVNode("small", { class: "text-muted ms-2" }, "Loading variables...", -1)
                        ]))]))
                      : (variableErrors.value.has(category._id))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            _createElementVNode("small", _hoisted_15, [
                              _cache[8] || (_cache[8] = _createElementVNode("i", { class: "fa fa-exclamation-circle me-1" }, null, -1)),
                              _createTextVNode(" " + _toDisplayString(variableErrors.value.get(category._id)), 1)
                            ])
                          ]))
                        : (categoryVariables.value.get(category._id)?.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categoryVariables.value.get(category._id), (variable) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: variable._id,
                                  class: "variable-item",
                                  draggable: "true",
                                  onDragstart: ($event: any) => (handleDragStart(variable, $event))
                                }, [
                                  _createElementVNode("div", _hoisted_18, [
                                    _cache[9] || (_cache[9] = _createElementVNode("i", { class: "fa fa-grip-vertical drag-handle me-2" }, null, -1)),
                                    _createElementVNode("div", _hoisted_19, [
                                      _createElementVNode("div", _hoisted_20, _toDisplayString(variable.title), 1),
                                      (variable.description)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(variable.description), 1))
                                        : _createCommentVNode("", true)
                                    ])
                                  ]),
                                  _createElementVNode("div", _hoisted_22, [
                                    (variable.slug)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(variable.slug), 1))
                                      : _createCommentVNode("", true)
                                  ])
                                ], 40, _hoisted_17))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_24, [...(_cache[10] || (_cache[10] = [
                              _createElementVNode("small", { class: "text-muted" }, [
                                _createElementVNode("i", { class: "fa fa-info-circle me-1" }),
                                _createTextVNode(" No variables in this category ")
                              ], -1)
                            ]))]))
                  ]))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})